import React, { useState } from 'react';
import { useLanguage } from '../i18n';

// --- Flygplatser, regioner ---
const airportList = [
  // Stockholm
  { code: "ESSU", name: "Eskilstuna", region: "Stockholm" },
  { code: "ESSB", name: "Stockholm-Bromma", region: "Stockholm" },
  { code: "ESSA", name: "Stockholm-Arlanda", region: "Stockholm" },
  { code: "ESKN", name: "Stockholm-Skavsta", region: "Stockholm" },
  { code: "ESOW", name: "Stockholm-Västerås", region: "Stockholm" },
  { code: "ESCM", name: "Uppsala", region: "Stockholm" },
  // Norrland
  { code: "ESNX", name: "Arvidsjaur", region: "Norrland" },
  { code: "ESNG", name: "Gällivare", region: "Norrland" },
  { code: "ESNQ", name: "Kiruna", region: "Norrland" },
  { code: "ESNK", name: "Kramfors-Sollefteå", region: "Norrland" },
  { code: "ESPA", name: "Luleå-Kallax", region: "Norrland" },
  { code: "ESNL", name: "Lycksele", region: "Norrland" },
  { code: "ESUP", name: "Pajala", region: "Norrland" },
  { code: "ESNN", name: "Sundsvall-Timrå", region: "Norrland" },
  { code: "ESND", name: "Sveg", region: "Norrland" },
  { code: "ESNU", name: "Umeå", region: "Norrland" },
  { code: "ESNV", name: "Vilhelmina", region: "Norrland" },
  { code: "ESPE", name: "Vidsel", region: "Norrland" },
  { code: "ESNZ", name: "Åre Östersund", region: "Norrland" },
  { code: "ESNO", name: "Örnsköldsvik", region: "Norrland" },
  // Mellansverige
  { code: "ESSD", name: "Borlänge", region: "Mellansverige" },
  { code: "ESOH", name: "Hagfors", region: "Mellansverige" },
  { code: "ESMV", name: "Hagshult", region: "Mellansverige" },
  { code: "ESIA", name: "Karlsborg", region: "Mellansverige" },
  { code: "ESOK", name: "Karlstad", region: "Mellansverige" },
  { code: "ESKM", name: "Mora-Siljan", region: "Mellansverige" },
  { code: "ESKS", name: "Sälen-Scandinavian Mountains", region: "Mellansverige" },
  { code: "ESST", name: "Torsby", region: "Mellansverige" },
  // Östsverige
  { code: "ESMQ", name: "Kalmar", region: "Östsverige" },
  { code: "ESCF", name: "Linköping-Malmen", region: "Östsverige" },
  { code: "ESSL", name: "Linköping-Saab", region: "Östsverige" },
  { code: "ESSP", name: "Norrköping-Kungsängen", region: "Östsverige" },
  { code: "ESSV", name: "Visby", region: "Östsverige" },
  // Västsverige
  { code: "ESMT", name: "Halmstad", region: "Västsverige" },
  { code: "ESGG", name: "Göteborg-Landvetter", region: "Västsverige" },
  { code: "ESGT", name: "Göteborg-Stallbacka", region: "Västsverige" },
  { code: "ESGP", name: "Göteborg-Säve", region: "Västsverige" },
  { code: "ESGJ", name: "Jönköping", region: "Västsverige" },
  { code: "ESTL", name: "Ljungbyhed", region: "Västsverige" },
  { code: "ESIB", name: "Såtenäs", region: "Västsverige" },
  { code: "ESGR", name: "Trollhättan Vänersborg", region: "Västsverige" },
  // Sydsverige
  { code: "ESMS", name: "Malmö", region: "Sydsverige" },
  { code: "ESMK", name: "Kristianstad", region: "Sydsverige" },
  { code: "ESDF", name: "Ronneby", region: "Sydsverige" },
  { code: "ESMX", name: "Växjö-Kronoberg", region: "Sydsverige" },
  { code: "ESTA", name: "Ängelholm", region: "Sydsverige" }
];
const regions = [...new Set(airportList.map(a => a.region))];

// --- MultiSelect-komponent för flygplatser ---
function AirportMultiSelect({
  selectedCodes = [],
  setSelectedCodes,
  required = false,
  minSelection = 1,
}) {
  const { t } = useLanguage();
  const [search, setSearch] = useState('');
  const [focusIdx, setFocusIdx] = useState(-1);

  const regionLabels = {
    "Stockholm": t('regionStockholm'),
    "Sydsverige": t('regionSouthSweden'),
    "Västsverige": t('regionWestSweden'),
    "Norrland": t('regionNorthSweden'),
    "Östsverige": t('regionEastSweden'),
    "Mellansverige": t('regionCentralSweden'),
  };

  function getFiltered() {
    return airportList.filter(
      a => (a.code + " " + a.name)
        .toLowerCase()
        .includes(search.trim().toLowerCase())
    );
  }

  function groupedAirports() {
    const filtered = getFiltered();
    return regions.map(region => ({
      region,
      airports: filtered.filter(a => a.region === region),
    })).filter(g => g.airports.length > 0);
  }

  const allVisible = getFiltered().length > 0 && getFiltered().every(a => selectedCodes.includes(a.code));
  function toggleAll() {
    const filtered = getFiltered();
    if (allVisible) {
      setSelectedCodes(selectedCodes.filter(code => !filtered.map(f => f.code).includes(code)));
    } else {
      setSelectedCodes([
        ...selectedCodes,
        ...filtered.map(a => a.code).filter(code => !selectedCodes.includes(code)),
      ]);
    }
  }

  function handleKeyDown(e) {
    const filtered = getFiltered();
    if (!filtered.length) return;
    if (e.key === "ArrowDown") setFocusIdx(idx => (idx + 1) % filtered.length);
    if (e.key === "ArrowUp") setFocusIdx(idx => (idx > 0 ? idx - 1 : filtered.length - 1));
    if (e.key === "Enter" && focusIdx >= 0) {
      const code = filtered[focusIdx].code;
      setSelectedCodes(codes =>
        codes.includes(code)
          ? codes.filter(c => c !== code)
          : [...codes, code]
      );
    }
  }

  const countText =
    selectedCodes.length === 0
      ? t('noAirportsSelected')
      : selectedCodes.length === 1
      ? t('oneAirportSelected')
      : t('manyAirportsSelected', { count: selectedCodes.length });

  const valid = !required || selectedCodes.length >= minSelection;

  return (
    <div
      style={{
        border: "1px solid #eee",
        borderRadius: "8px",
        background: "#e9f6e6",
        padding: "8px",
        maxHeight: "320px",
        overflowY: "auto",
        position: "relative",
      }}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          background: "#e9f6e6",
          zIndex: 10,
          paddingBottom: 6,
          marginBottom: 6,
        }}
      >
        <input
          placeholder={t('searchAirportPlaceholder')}
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setFocusIdx(-1);
          }}
          style={{
            width: "100%",
            marginBottom: 6,
            padding: "4px 8px",
            borderRadius: 6,
            border: "1px solid #b7d9b7",
            fontSize: 14,
          }}
        />
        <button
          type="button"
          onClick={toggleAll}
          style={{
            fontSize: 13,
            color: "#2b6cb0",
            background: "none",
            border: "none",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {allVisible ? t('deselectAll') : t('selectAll')}
        </button>
        <span style={{ float: "right", fontSize: 12, color: "#555" }}>
          {countText}
        </span>
      </div>
      {groupedAirports().map(group => (
        <div key={group.region}>
          <div
            style={{
              fontWeight: "bold",
              marginTop: 8,
              marginBottom: 2,
              color: "#1e3a8a",
              fontSize: 13,
            }}
          >
            {regionLabels[group.region] || group.region}
          </div>
          {group.airports.map((a, idx) => (
            <label
              key={a.code}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "4px",
                fontSize: "14px",
                cursor: "pointer",
                background: focusIdx === idx ? "#cfe6c2" : "none",
                borderRadius: focusIdx === idx ? 4 : 0,
                padding: focusIdx === idx ? "2px 4px" : "0",
              }}
            >
              <input
                type="checkbox"
                checked={selectedCodes.includes(a.code)}
                onChange={e =>
                  setSelectedCodes(codes =>
                    e.target.checked
                      ? [...codes, a.code]
                      : codes.filter(c => c !== a.code)
                  )
                }
                style={{ accentColor: "#16a34a", marginRight: 6 }}
                tabIndex={-1}
              />
              <span style={{ fontWeight: 500 }}>{a.code}</span>
              <span style={{ color: "#64748b", marginLeft: 7 }}>{a.name}</span>
            </label>
          ))}
        </div>
      ))}
      {!valid && (
        <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 6 }}>
          {t('chooseMinAirports', { min: minSelection })}
        </div>
      )}
      {valid && selectedCodes.length > 0 && (
        <div style={{ color: "#16a34a", fontSize: 12, marginTop: 6 }}>
          {t('done')}
        </div>
      )}
    </div>
  );
}

// --- RegisterModal huvudkomponent ---
export default function RegisterModal({ open, onClose, onSuccess, onSwitchToLogin }) {
  const { t, language } = useLanguage();

  const [company, setCompany] = useState(true);
  const [formData, setFormData] = useState({
    companyName: '',
    orgNr: '',
    property: '',
    contactPerson: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: '',
    zipcode: '',
    city: ''
  });

  const [airportCodes, setAirportCodes] = useState([]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('passwordMismatch'));
      setLoading(false);
      return;
    }
    if (!formData.email || !formData.contactPerson || !formData.password || !acceptedTerms) {
      setError(t('requiredField'));
      setLoading(false);
      return;
    }
    if (company && (!formData.companyName || !formData.orgNr)) {
      setError(t('requiredField'));
      setLoading(false);
      return;
    }
    if (airportCodes.length === 0) {
      setError(language === "sv" ? "Välj minst en flygplats." : "Please select at least one airport.");
      setLoading(false);
      return;
    }

    const registrationData = {
      ...formData,
      isCompany: company,
      acceptedTerms,
      airportCode: airportCodes.join(',')
    };

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(registrationData)
      });
      const data = await response.json();

      if (response.ok) {
        onSuccess(data.user);
        onClose();
        setFormData({
          companyName: '',
          orgNr: '',
          property: '',
          contactPerson: '',
          email: '',
          password: '',
          confirmPassword: '',
          phone: '',
          address: '',
          zipcode: '',
          city: ''
        });
        setAirportCodes([]);
        setAcceptedTerms(false);
      } else {
        setError(data.error || t('registrationFailed'));
      }
    } catch {
      setError(t('registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  const canSubmit =
    formData.email &&
    formData.contactPerson &&
    formData.password &&
    formData.confirmPassword &&
    acceptedTerms &&
    (company ? formData.companyName && formData.orgNr : true) &&
    airportCodes.length > 0;

  // --- GDPR / Terms Text Block ---
  const termsText = language === 'sv' ? (
    <div style={{ background: '#f8f8e8', padding: 10, borderRadius: 6, marginTop: 10, fontSize: 14 }}>
      <b>Villkor, GDPR & ansvarsfriskrivning:</b>
      <ul style={{ margin: '8px 0 0 0' }}>
        <li>
          Jag godkänner att de uppgifter jag lämnar i detta formulär – såsom namn, e-postadress, analysdata och IP-adress – sparas av Flightsafe.se i syfte att hantera min registrering och möjliggöra effektiv kundservice. Uppgifterna behandlas konfidentiellt och sparas endast så länge det är nödvändigt för ärendets hantering eller enligt gällande lag.
        </li>
        <li>
          Jag kan när som helst begära rättelse eller radering av mina personuppgifter genom att kontakta kundtjänst.
        </li>
        <li>
          Jag har rätt att få tillgång till de uppgifter som Flightsafe.se har sparat om mig, invända mot behandlingen eller begära begränsning. Jag kan också lämna klagomål till Integritetsskyddsmyndigheten (IMY).
        </li>
        <li>
          För frågor kring personuppgiftsbehandling, vänligen kontakta: kundtjanst@flightsafe.se
        </li>
        <li>
          <b>Ansvarsfriskrivning:</b> Flightsafe.se tar inget ansvar för den information, analys eller de resultat som genereras av programmet. Det är ditt ansvar som användare att noggrant kontrollera såväl den data du matar in som de analyser och resultat som programmet levererar.
        </li>
      </ul>
    </div>
  ) : (
    <div style={{ background: '#f8f8e8', padding: 10, borderRadius: 6, marginTop: 10, fontSize: 14 }}>
      <b>Terms, GDPR & Disclaimer:</b>
      <ul style={{ margin: '8px 0 0 0' }}>
        <li>
          I consent to the information I provide in this form – such as name, email address, analysis data, and IP address – being stored by Flightsafe.se for the purpose of managing my registration and enabling effective customer service. The information is treated confidentially and stored only as long as necessary for handling the case or as required by law.
        </li>
        <li>
          I can at any time request correction or deletion of my personal data by contacting customer service.
        </li>
        <li>
          I have the right to access my stored data, object to the processing, or request restriction. I can also file a complaint with the Swedish Authority for Privacy Protection (IMY).
        </li>
        <li>
          For questions regarding data processing, please contact: kundtjanst@flightsafe.se
        </li>
        <li>
          <b>Disclaimer:</b> Flightsafe.se assumes no responsibility for the information, analyses, or results generated by the program. It is your responsibility as a user to carefully verify both the data you input and the analyses and results provided by the program.
        </li>
      </ul>
    </div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal-content narrow" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2 className="title">{t('registerTitle')}</h2>
          <button
            type="button"
            className="icon-close"
            aria-label={language === 'sv' ? 'Stäng' : 'Close'}
            onClick={onClose}
            title={language === 'sv' ? 'Stäng' : 'Close'}
          >
            ×
          </button>
        </div>
        <div className="modal-body-scroll">
          <form onSubmit={handleSubmit} className="auth-form compact-grid">
            {error && <div className="error-message">{error}</div>}

            {/* 1. Email */}
            <div className="row row-1">
              <div className="cell">
                <label className="lbl">
                  {language === 'sv'
                    ? 'E-postadress (används för inloggning) *'
                    : 'Email address (used for login) *'}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="inp"
                  autoComplete="email"
                />
              </div>
            </div>
            {/* 2. Lösenord + Bekräfta */}
            <div className="row row-2">
              <div className="cell">
                <label className="lbl">{t('password')} *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="inp"
                  autoComplete="new-password"
                />
              </div>
              <div className="cell">
                <label className="lbl">{t('confirmPassword')} *</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="inp"
                  autoComplete="new-password"
                />
              </div>
            </div>
            {/* 3. Radio */}
            <div className="row row-1">
              <div className="cell">
                <label className="lbl">
                  {language === 'sv'
                    ? 'Registrerar du som företag eller privatperson?'
                    : 'Are you registering as a company or private individual?'}
                </label>
                <div className="radio-options">
                  <label>
                    <input
                      type="radio"
                      name="company"
                      checked={company}
                      onChange={() => setCompany(true)}
                    />
                    {language === 'sv' ? 'Företag' : 'Company'}
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="company"
                      checked={!company}
                      onChange={() => setCompany(false)}
                    />
                    {language === 'sv' ? 'Privatperson' : 'Private individual'}
                  </label>
                </div>
              </div>
            </div>
            {/* 4. Företagsnamn + Org.nr */}
            {company && (
              <div className="row row-2">
                <div className="cell">
                  <label className="lbl">
                    {language === 'sv' ? 'Företagsnamn *' : 'Company name *'}
                  </label>
                  <input
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    className="inp"
                    autoComplete="organization"
                  />
                </div>
                <div className="cell">
                  <label className="lbl">
                    {language === 'sv' ? 'Org. nummer *' : 'Org. number *'}
                  </label>
                  <input
                    name="orgNr"
                    value={formData.orgNr}
                    onChange={handleChange}
                    required
                    className="inp"
                  />
                </div>
              </div>
            )}
            {/* 5. Fastighet */}
            {company && (
              <div className="row row-1">
                <div className="cell">
                  <label className="lbl">
                    {language === 'sv' ? 'Fastighet (frivillig)' : 'Property (optional)'}
                  </label>
                  <input
                    name="property"
                    value={formData.property}
                    onChange={handleChange}
                    className="inp"
                  />
                </div>
              </div>
            )}
            {/* 6. Kontaktperson + Telefon */}
            <div className="row row-2">
              <div className="cell">
                <label className="lbl">
                  {language === 'sv' ? 'Kontaktperson *' : 'Contact person *'}
                </label>
                <input
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  required
                  className="inp"
                  autoComplete="name"
                />
              </div>
              <div className="cell">
                <label className="lbl">{language === 'sv' ? 'Telefonnummer' : 'Phone'}</label>
                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="inp"
                  autoComplete="tel"
                />
              </div>
            </div>
            {/* 7. Adress + Postnummer + Postort */}
            <div className="row row-3">
              <div className="cell">
                <label className="lbl">{language === 'sv' ? 'Adress' : 'Address'}</label>
                <input
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="inp"
                  autoComplete="street-address"
                />
              </div>
              <div className="cell">
                <label className="lbl">
                  {language === 'sv' ? 'Postnummer' : 'Zipcode'}
                </label>
                <input
                  name="zipcode"
                  value={formData.zipcode}
                  onChange={handleChange}
                  className="inp"
                  autoComplete="postal-code"
                />
              </div>
              <div className="cell">
                <label className="lbl">{language === 'sv' ? 'Postort' : 'City'}</label>
                <input
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="inp"
                  autoComplete="address-level2"
                />
              </div>
            </div>
            {/* 8. Flygplatsmultiselect */}
            <div className="row row-1">
              <div className="cell">
                <label className="lbl">{t('airportAccess')}</label>
                <small className="help">{t('airportAccessHelp')}</small>
                <AirportMultiSelect
                  selectedCodes={airportCodes}
                  setSelectedCodes={setAirportCodes}
                  required={true}
                  minSelection={1}
                />
              </div>
            </div>
            {/* 9. Villkor med GDPR/ansvar */}
            <div className="checkbox-group compact">
              <label>
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  required
                />{' '}
                {language === 'sv'
                  ? 'Jag har tagit del av villkor, dataskydd (GDPR), och ansvarsfriskrivning nedan och accepterar dem.'
                  : 'I have read and accept the terms, data protection (GDPR), and disclaimer below.'}
              </label>
            </div>
            {termsText}
            {/* 10. Actions */}
            <div className="form-actions">
              <button type="submit" disabled={loading || !canSubmit} className="submit-button">
                {loading ? t('loading') : t('registerButton')}
              </button>
            </div>
            <div className="auth-switch">
              <p>
                {t('alreadyHaveAccount')}{' '}
                <button type="button" className="link-button" onClick={onSwitchToLogin}>
                  {t('clickHereToLogin')}
                </button>
              </p>
            </div>
          </form>
        </div>
        {/* --- Styles --- */}
        <style>{`
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.45);
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 40px 16px;
          z-index: 1000;
        }
        .modal-content.narrow {
          --brand: #2b6cb0;
          --brand-ink: #1e3a8a;
          --field-bg: #e9f6e6;
          --field-focus: #16a34a;
          --gap: 10px;
          --col: 280px;
          --wrap: calc(var(--col) * 2 + var(--gap));
          --col3: calc((var(--wrap) - var(--gap) * 2) / 3);
          width: 100%;
          max-width: 700px;
          min-height: 650px;
          border-radius: 12px;
          overflow: hidden;
          background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
          border: 1px solid #e5e7eb;
          box-shadow: 0 20px 40px rgba(0,0,0,0.18), 0 4px 10px rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          max-height: 96vh;
        }
        .modal-header {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px;
          background: linear-gradient(180deg, #f1f5ff, #f8fbff);
          border-bottom: 1px solid #e3e8f0;
        }
        .modal-body-scroll {
          flex: 1 1 auto;
          overflow-y: auto;
          padding-bottom: 20px;
        }
        .title {
          margin: 0;
          font-size: 18px;
          color: var(--brand-ink);
          font-weight: 600;
        }
        .icon-close {
          width: 32px;
          height: 32px;
          line-height: 28px;
          text-align: center;
          border-radius: 8px;
          font-size: 22px;
          border: 1px solid transparent;
          color: #475569;
          background: transparent;
          cursor: pointer;
        }
        .icon-close:hover {
          background: #eef2ff;
          border-color: #dbe3ff;
          color: #1f2937;
        }
        .auth-form.compact-grid {
          padding: 12px;
        }
        .error-message {
          color: #b91c1c;
          background: #fee2e2;
          border: 1px solid #fecaca;
          border-radius: 6px;
          padding: 8px 10px;
          font-size: 13px;
          width: var(--wrap);
          margin: 0 auto 8px auto;
        }
        .row {
          display: grid;
          width: var(--wrap);
          column-gap: var(--gap);
          row-gap: 6px;
          margin: 0 auto 8px auto;
          align-items: end;
        }
        .row-1 { grid-template-columns: var(--wrap); }
        .row-2 { grid-template-columns: var(--col) var(--col); }
        .row-3 { grid-template-columns: var(--col3) var(--col3) var(--col3); }
        .cell { display: flex; flex-direction: column; }
        .lbl { font-size: 13px; color: #334155; margin-bottom: 3px; }
        .help {
          display: block;
          font-size: 12px;
          color: #64748b;
          margin: 2px 0 6px 0;
        }
        .inp {
          width: 100%;
          padding: 7px 10px;
          font-size: 14px;
          border: 1px solid #b7d9b7;
          border-radius: 8px;
          box-sizing: border-box;
          background: var(--field-bg);
          color: #0f172a;
          transition: box-shadow .15s ease, border-color .15s ease, background .15s ease;
        }
        .inp::placeholder { color: #6b7280; opacity: .8; }
        .inp:focus {
          outline: none;
          border-color: var(--field-focus);
          box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.20);
          background: #ffffff;
        }
        .inp:disabled {
          background: #eef2f4;
          border-color: #d1d5db;
          color: #6b7280;
          cursor: not-allowed;
        }
        .radio-options {
          display: inline-flex;
          gap: 16px;
          margin-top: 2px;
          align-items: center;
          font-size: 14px;
          color: #1f2937;
        }
        .checkbox-group.compact {
          width: var(--wrap);
          margin: 8px auto 10px auto;
          font-size: 12.5px;
          line-height: 1.35;
          color: #374151;
        }
        .form-actions {
          display: flex;
          justify-content: center;
          margin: 10px auto 6px auto;
          width: var(--wrap);
        }
        .submit-button {
          background: var(--brand);
          color: #fff;
          font-size: 15px;
          border-radius: 999px;
          padding: 9px 22px;
          border: none;
          cursor: pointer;
          transition: filter .15s ease, transform .02s ease, box-shadow .15s ease;
          box-shadow: 0 4px 10px rgba(43,108,176,0.18);
        }
        .submit-button:hover { filter: brightness(1.06); box-shadow: 0 6px 14px rgba(43,108,176,0.24); }
        .submit-button:active { transform: translateY(1px); }
        .submit-button:disabled { opacity: .6; cursor: not-allowed; box-shadow: none; }
        .auth-switch {
          margin: 8px auto 4px auto;
          width: var(--wrap);
          text-align: center;
          font-size: 14px;
        }
        .link-button {
          background: none;
          color: var(--brand);
          border: none;
          cursor: pointer;
          text-decoration: underline;
          font-size: 14px;
        }
        @media (max-width: 900px) {
          .modal-content.narrow { max-width: 98vw; }
        }
        @media (max-width: 640px) {
          .modal-content.narrow { --col: 200px; }
        }
        @media (max-width: 560px) {
          .modal-content.narrow { --col: 180px; }
        }
        `}</style>
      </div>
    </div>
  );
}